// Per-symbol render-version scheduling for the bookStore. Split out of
// bookStoreInternal.ts so every book module stays under 250 LOC. This is the
// EXACT rAF-coalescing logic from the old singleton (bookStore.ts:285-314),
// only re-scoped: the frame/timeout ids live on the symbol's runtime and only
// that symbol's renderVersion is bumped.

import type {
  BookStoreSet,
  BookStoreGet,
  SymbolBookRuntime,
} from './bookStoreTypes';

/**
 * rAF-coalesce a renderVersion bump for a single symbol. Identical scheduling
 * to the old global scheduleRenderVersion, but the frame/timeout ids live on
 * this symbol's runtime and only that symbol's renderVersion is bumped.
 */
export function scheduleRenderVersion(
  symbol: string,
  runtime: SymbolBookRuntime,
  set: BookStoreSet,
  get: BookStoreGet,
): void {
  if (runtime.renderFramePending) return;
  runtime.renderFramePending = true;

  const flush = () => {
    runtime.renderFramePending = false;
    runtime.renderFrameId = null;
    runtime.renderTimeoutId = null;
    set((s) => {
      const entry = get().bySymbol[symbol];
      if (!entry) return { bySymbol: s.bySymbol };
      return {
        bySymbol: {
          ...s.bySymbol,
          [symbol]: { ...entry, renderVersion: entry.renderVersion + 1 },
        },
      };
    });
  };

  if (typeof globalThis.requestAnimationFrame === 'function') {
    runtime.renderFrameId = globalThis.requestAnimationFrame(flush);
    return;
  }

  runtime.renderTimeoutId = setTimeout(flush, 0);
}

/**
 * Cancel a symbol's pending rAF/timeout and drop its runtime entry. Discards
 * lastSnapshotTs + pending-removal maps for that symbol (the per-symbol
 * equivalent of the old clear() resetting the module globals).
 */
export function cancelRuntime(
  symbol: string,
  runtimes: Map<string, SymbolBookRuntime>,
): void {
  const runtime = runtimes.get(symbol);
  if (!runtime) return;
  if (
    runtime.renderFrameId != null &&
    typeof globalThis.cancelAnimationFrame === 'function'
  ) {
    globalThis.cancelAnimationFrame(runtime.renderFrameId);
  }
  if (runtime.renderTimeoutId != null) {
    clearTimeout(runtime.renderTimeoutId);
  }
  runtimes.delete(symbol);
}
